import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import TopBar from '../../global/TopBar';
import { ShoppingCartIcon, XMarkIcon, PlusIcon, MinusIcon, TrashIcon, Bars3Icon, ChevronDownIcon } from '@heroicons/react/24/solid';
import { useCart } from '../CartContext';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector } from 'react-redux';

const Storefront = () => {
  const { storeId } = useParams();
  const [storeData, setStoreData] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCategoryMenuOpen, setIsCategoryMenuOpen] = useState(false);
  const { cart, addToCart, removeFromCart, updateQuantity } = useCart();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const token = useSelector((state) => state.auth.token);

  useEffect(() => {
    fetchStoreData();
  }, [storeId]);

  const fetchStoreData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_STORE_API_URL}/store/jewelry/${storeId}`,
        {
          headers: {
            "Content-Type": "application/json",
            "Api-Name": "store-api",
            "Authorization": `Bearer ${token}`
          }
        }
      );
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      setStoreData(data.store);
      setSelectedCategory(data.store.categories[0]);
      setError(null);
    } catch (err) {
      setError('Failed to fetch store data. Please try again later.');
      console.error('Error fetching store data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (item) => {
    addToCart(item, 1);
  };

  const getCartItemQuantity = (itemId) => {
    const cartItem = cart.find(item => item.id === itemId);
    return cartItem ? cartItem.quantity : 0;
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + item.price * item.quantity, 0).toFixed(2);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pri-dark to-sec-dark flex items-center justify-center">
        <motion.div
          className="w-16 h-16 border-4 border-pri-yellow border-t-transparent rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pri-dark to-sec-dark flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-sec-dark/50 backdrop-blur-sm p-8 rounded-xl shadow-2xl text-center"
        >
          <h2 className="text-2xl font-bold mb-4 text-pri-white">Oops!</h2>
          <p className="text-pri-gray">{error}</p>
        </motion.div>
      </div>
    );
  }

  if (!storeData) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-pri-dark to-sec-dark text-pri-white">
      <TopBar />
      
      {/* Hero Section */}
      <motion.div className="relative h-40 lg:h-96">
        <div className="absolute inset-0 overflow-hidden">
          <motion.img 
            src={storeData.bannerImage} 
            alt={storeData.storeName} 
            className="w-full h-full object-cover"
            initial={{ scale: 1.2 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.5 }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-pri-dark via-pri-dark/50 to-transparent" />
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="container mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-pri-white mb-2">
              {storeData.storeName}
            </h1>
            <p className="text-pri-gray text-lg md:text-xl">
              Exquisite jewelry for every occasion
            </p>
          </motion.div>
        </div>
      </motion.div>

      <div className="container mx-auto px-4 py-8">
        {/* Mobile Controls */}
        <div className="flex items-center justify-between mb-6 lg:hidden">
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsCategoryMenuOpen(!isCategoryMenuOpen)}
            className="flex items-center space-x-2 bg-sec-dark/50 backdrop-blur-sm px-4 py-2 rounded-xl"
          >
            <Bars3Icon className="h-5 w-5 text-pri-yellow" />
            <span className="text-pri-white font-medium">{selectedCategory?.name}</span>
            <ChevronDownIcon className={`h-5 w-5 text-pri-yellow transition-transform duration-300 ${isCategoryMenuOpen ? 'rotate-180' : ''}`} />
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsCartOpen(true)}
            className="relative"
          >
            <div className="bg-gradient-to-r from-pri-yellow to-pri-green p-3 rounded-full">
              <ShoppingCartIcon className="h-6 w-6 text-pri-dark" />
            </div>
            {cart.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-pri-red text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                {cart.length}
              </span>
            )}
          </motion.button>
        </div>

        {/* Mobile Category Menu */}
        <AnimatePresence>
          {isCategoryMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="lg:hidden mb-6"
            >
              <div className="bg-sec-dark/50 backdrop-blur-sm rounded-xl overflow-hidden">
                {storeData.categories.map(category => (
                  <motion.button
                    key={category.name}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setSelectedCategory(category);
                      setIsCategoryMenuOpen(false);
                    }}
                    className={`w-full text-left px-4 py-3 transition-colors ${
                      selectedCategory?.name === category.name 
                        ? 'bg-gradient-to-r from-pri-yellow to-pri-green text-pri-dark font-semibold'
                        : 'text-pri-white hover:bg-ter-dark'
                    }`}
                  >
                    {category.name}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Desktop Layout */}
        <div className="hidden lg:flex justify-end mb-6">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsCartOpen(true)}
            className="bg-gradient-to-r from-pri-yellow to-pri-green rounded-full py-2 px-6 flex items-center space-x-2"
          >
            <ShoppingCartIcon className="h-5 w-5 text-pri-dark" />
            <span className="text-pri-dark font-semibold">Cart ({cart.length})</span>
          </motion.button>
        </div>

        <div className="hidden lg:grid lg:grid-cols-4 gap-8">
          {/* Categories Sidebar */}
          <motion.div className="lg:col-span-1">
            <div className="bg-sec-dark/50 backdrop-blur-sm rounded-xl p-4">
              <h2 className="text-2xl font-bold mb-4 text-pri-white">Categories</h2>
              <ul className="space-y-2">
                {storeData.categories.map(category => (
                  <motion.li
                    key={category.name}
                    whileHover={{ scale: 1.02, x: 5 }}
                    whileTap={{ scale: 0.98 }}
                    className={`cursor-pointer py-3 px-4 rounded-lg transition-all duration-300 ${
                      selectedCategory?.name === category.name 
                        ? 'bg-gradient-to-r from-pri-yellow to-pri-green text-pri-dark font-semibold shadow-lg' 
                        : 'bg-ter-dark/50 text-pri-white hover:bg-ter-dark'
                    }`}
                    onClick={() => setSelectedCategory(category)}
                  >
                    {category.name}
                  </motion.li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Products Grid */}
          <div className="lg:col-span-3">
            <motion.div
              className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6"
            >
              {selectedCategory?.items.map(item => (
                <motion.div
                  key={item.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  whileHover={{ y: -5 }}
                  className="bg-sec-dark/50 backdrop-blur-sm rounded-lg sm:rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300"
                >
                  <div className="relative overflow-hidden group">
                    <motion.img 
                      src={item.image} 
                      alt={item.name} 
                      className="w-full h-32 sm:h-48 object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0" />
                  </div>

                  <div className="p-3 sm:p-4">
                    <div className="flex justify-between items-start gap-2 mb-2">
                      <h3 className="text-sm sm:text-lg font-bold text-pri-white truncate">
                        {item.name}
                      </h3>
                      <span className="bg-gradient-to-r from-pri-yellow to-pri-green text-pri-dark px-2 py-0.5 rounded-full text-xs font-bold whitespace-nowrap">
                        £{item.price.toFixed(2)}
                      </span>
                    </div>

                    <p className="text-xs sm:text-sm text-pri-gray mb-3 line-clamp-2">
                      {item.description}
                    </p>

                    {getCartItemQuantity(item.id) > 0 ? (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center bg-ter-dark rounded-lg overflow-hidden">
                          <motion.button
                            whileTap={{ scale: 0.9 }}
                            onClick={() => updateQuantity(item.id, Math.max(1, getCartItemQuantity(item.id) - 1))}
                            className="bg-pri-yellow text-pri-dark p-1.5 sm:p-2"
                          >
                            <MinusIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                          </motion.button>
                          <span className="px-2 sm:px-4 py-1 text-pri-white text-sm font-medium">
                            {getCartItemQuantity(item.id)}
                          </span>
                          <motion.button
                            whileTap={{ scale: 0.9 }}
                            onClick={() => updateQuantity(item.id, getCartItemQuantity(item.id) + 1)}
                            className="bg-pri-yellow text-pri-dark p-1.5 sm:p-2"
                          >
                            <PlusIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                          </motion.button>
                        </div>
                        <motion.button
                          whileTap={{ scale: 0.9 }}
                          onClick={() => removeFromCart(item.id)}
                          className="bg-pri-red/10 text-pri-red p-1.5 sm:p-2 rounded-lg"
                        >
                          <TrashIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                        </motion.button>
                      </div>
                    ) : (
                      <motion.button
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleAddToCart(item)}
                        className="w-full bg-gradient-to-r from-pri-yellow to-pri-green text-pri-dark py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg text-sm font-semibold"
                      >
                        Add to Cart
                      </motion.button>
                    )}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Mobile Products Grid */}
        <div className="lg:hidden">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-2 gap-3"
          >
            {selectedCategory?.items.map(item => (
              <motion.div
                key={item.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="bg-sec-dark/50 backdrop-blur-sm rounded-lg overflow-hidden shadow-lg"
              >
                <div className="relative overflow-hidden">
                  <img 
                    src={item.image} 
                    alt={item.name} 
                    className="w-full h-32 object-cover"
                  />
                </div>
                
                <div className="p-3">
                  <div className="flex justify-between items-start gap-2 mb-2">
                    <h3 className="text-sm font-bold text-pri-white truncate">
                      {item.name}
                    </h3>
                    <span className="bg-gradient-to-r from-pri-yellow to-pri-green text-pri-dark px-2 py-0.5 rounded-full text-xs font-bold whitespace-nowrap">
                      £{item.price.toFixed(2)}
                    </span>
                  </div>

                  {getCartItemQuantity(item.id) > 0 ? (
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center bg-ter-dark rounded-lg overflow-hidden">
                        <motion.button
                          whileTap={{ scale: 0.9 }}
                          onClick={() => updateQuantity(item.id, Math.max(1, getCartItemQuantity(item.id) - 1))}
                          className="bg-pri-yellow text-pri-dark p-1.5"
                        >
                          <MinusIcon className="h-3 w-3" />
                        </motion.button>
                        <span className="px-2 py-1 text-pri-white text-sm">
                          {getCartItemQuantity(item.id)}
                        </span>
                        <motion.button
                          whileTap={{ scale: 0.9 }}
                          onClick={() => updateQuantity(item.id, getCartItemQuantity(item.id) + 1)}
                          className="bg-pri-yellow text-pri-dark p-1.5"
                        >
                          <PlusIcon className="h-3 w-3" />
                        </motion.button>
                      </div>
                      <motion.button
                        whileTap={{ scale: 0.9 }}
                        onClick={() => removeFromCart(item.id)}
                        className="bg-pri-red/10 text-pri-red p-1.5 rounded-lg"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </motion.button>
                    </div>
                  ) : (
                    <motion.button
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleAddToCart(item)}
                      className="w-full bg-gradient-to-r from-pri-yellow to-pri-green text-pri-dark py-1.5 px-3 rounded-lg text-sm font-semibold mt-2"
                    >
                      Add to Cart
                    </motion.button>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Cart Slide-over */}
      <AnimatePresence>
        {isCartOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-pri-dark/80 backdrop-blur-sm z-50"
            onClick={() => setIsCartOpen(false)}
          >
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30 }}
              className="absolute inset-y-0 right-0 max-w-md w-full bg-gradient-to-br from-sec-dark to-ter-dark shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              {/* Cart Header */}
              <div className="px-6 py-4 bg-pri-dark/30 backdrop-blur-sm border-b border-pri-gray/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <ShoppingCartIcon className="h-6 w-6 text-pri-yellow" />
                    <h2 className="text-xl font-bold text-pri-white">Your Cart</h2>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setIsCartOpen(false)}
                    className="p-2 rounded-full hover:bg-pri-dark/30 text-pri-gray hover:text-pri-white transition-colors"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </motion.button>
                </div>
              </div>

              {/* Cart Items */}
              <div className="flex-1 overflow-y-auto">
                {cart.length === 0 ? (
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex flex-col items-center justify-center h-full p-4 sm:p-8 text-center"
                  >
                    <ShoppingCartIcon className="h-12 w-12 sm:h-16 sm:w-16 text-pri-gray mb-4" />
                    <p className="text-pri-gray text-base sm:text-lg font-medium">Your cart is empty</p>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setIsCartOpen(false)}
                      className="mt-4 text-pri-yellow hover:text-pri-green transition-colors"
                    >
                      Continue Shopping
                    </motion.button>
                  </motion.div>
                ) : (
                  <div className="p-2 sm:p-4">
                    <div className="grid grid-cols-2 gap-2 sm:gap-3">
                      {cart.map((item) => (
                        <motion.div
                          key={item.id}
                          layout
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -20 }}
                          className="bg-pri-dark/20 rounded-lg sm:rounded-xl p-2 sm:p-3 backdrop-blur-sm"
                        >
                          <div className="flex flex-col">
                            <div className="flex items-start space-x-2 mb-2">
                              <img 
                                src={item.image} 
                                alt={item.name}
                                className="w-16 h-16 rounded-md object-cover"
                              />
                              <div className="flex-1 min-w-0">
                                <h3 className="text-pri-white font-medium text-xs sm:text-sm truncate">
                                  {item.name}
                                </h3>
                                <p className="text-pri-gray text-xs mb-1">
                                  £{item.price.toFixed(2)} each
                                </p>
                                <div className="flex items-center bg-pri-dark/30 rounded-md overflow-hidden w-fit">
                                  <motion.button
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                    className="p-1 text-pri-yellow hover:bg-pri-dark/50 transition-colors"
                                  >
                                    <MinusIcon className="h-3 w-3" />
                                  </motion.button>
                                  <span className="px-2 text-pri-white font-medium text-xs min-w-[20px] text-center">
                                    {item.quantity}
                                  </span>
                                  <motion.button
                                    whileTap={{ scale: 0.9 }}
                                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                    className="p-1 text-pri-yellow hover:bg-pri-dark/50 transition-colors"
                                  >
                                    <PlusIcon className="h-3 w-3" />
                                  </motion.button>
                                </div>
                              </div>
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={() => removeFromCart(item.id)}
                                className="p-1 text-pri-red hover:bg-pri-red/10 rounded-md transition-colors"
                              >
                                <TrashIcon className="h-3 w-3 sm:h-4 sm:w-4" />
                              </motion.button>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Cart Footer */}
              {cart.length > 0 && (
                <div className="border-t border-pri-gray/10">
                  <div className="px-4 sm:px-6 py-3 sm:py-4 bg-pri-dark/30 backdrop-blur-sm">
                    <div className="flex items-center justify-between mb-3 sm:mb-4">
                      <span className="text-pri-gray text-sm sm:text-base">Subtotal</span>
                      <span className="text-pri-white font-bold text-sm sm:text-base">£{getTotalPrice()}</span>
                    </div>
                    <Link
                      to="/store/jewelry/checkout"
                      className="block w-full"
                    >
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="w-full bg-gradient-to-r from-pri-yellow to-pri-green text-pri-dark font-bold py-2 sm:py-3 px-4 rounded-lg sm:rounded-xl hover:shadow-lg transition-all duration-300 text-sm sm:text-base"
                      >
                        Proceed to Checkout
                      </motion.button>
                    </Link>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Storefront;
